import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/solid';

type ColorVariant = 'green' | 'blue' | 'purple' | 'amber' | 'rose';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: React.ElementType;
  color?: ColorVariant;
  loading?: boolean;
}

const iconColorClasses: Record<ColorVariant, string> = {
  green: 'bg-gradient-to-br from-kenagri-500 to-kenagri-600 shadow-glow-green',
  blue: 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-glow-blue',
  purple: 'bg-gradient-to-br from-purple-500 to-purple-600 shadow-[0_0_20px_-5px_rgb(139_92_246_/_0.4)]',
  amber: 'bg-gradient-to-br from-amber-500 to-amber-600 shadow-[0_0_20px_-5px_rgb(245_158_11_/_0.4)]',
  rose: 'bg-gradient-to-br from-rose-500 to-rose-600 shadow-[0_0_20px_-5px_rgb(244_63_94_/_0.4)]',
};

export default function StatCard({
  title,
  value,
  change,
  changeLabel = 'vs last week',
  icon: Icon,
  color = 'green',
  loading = false,
}: StatCardProps) {
  if (loading) {
    return <StatCardSkeleton />;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-6 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-200">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 tracking-tight">
            {value}
          </p>
          {change !== undefined && (
            <div className="mt-3 flex items-center gap-1.5">
              <span
                className={`
                  inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium
                  ${change >= 0
                    ? 'bg-green-50 text-green-700'
                    : 'bg-red-50 text-red-700'
                  }
                `}
              >
                {change >= 0 ? (
                  <ArrowUpIcon className="h-3 w-3" />
                ) : (
                  <ArrowDownIcon className="h-3 w-3" />
                )}
                {Math.abs(change)}%
              </span>
              <span className="text-xs text-gray-500">{changeLabel}</span>
            </div>
          )}
        </div>
        <div
          className={`
            p-3 rounded-xl
            ${iconColorClasses[color]}
          `}
        >
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
}

export function StatCardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-card p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="h-4 w-24 skeleton-shimmer rounded" />
          <div className="mt-3 h-8 w-20 skeleton-shimmer rounded" />
          <div className="mt-4 h-5 w-32 skeleton-shimmer rounded-full" />
        </div>
        <div className="h-12 w-12 skeleton-shimmer rounded-xl" />
      </div>
    </div>
  );
}
